/**
 * Cart Property Enricher
 *
 * This script enriches cart items with delivery properties when they're added by
 * third-party apps (like B2B Bulk Order / CSV Upload app) that don't include these properties.
 *
 * Optimized for performance:
 * - Uses debouncing to prevent rapid-fire API calls
 * - Batches updates using /cart/update.js where possible
 * - Polls at longer intervals (5 seconds)
 * - Caches product data to reduce API calls
 */

class CartPropertyEnricher {
  constructor() {
    this.enrichingItems = new Set();
    this.productDataCache = new Map();
    this.inflightProductRequests = new Map();
    this.lastCartItemCount = 0;
    this.isProcessing = false;
    this.pendingEnrichment = false;
    this.debounceTimer = null;
    this.enrichmentScheduled = false;
    this.enrichDebounceMs = 200;
    this.cartChangeConcurrency = 3;
    this.pollIntervalMs = 5000;
    this.cartPagePollIntervalMs = 2000;
    this.enrichmentResolvers = [];
    this.init();
  }

  init() {
    this.initCartUpdateSubscriber();
    this.initFetchInterceptor();
    this.initXHRInterceptor();
    this.initCartPolling();

    // Run initial check immediately on page load
    this.runInitialCheck();
  }

  /**
   * Run initial enrichment check on page load
   * This catches items that need enrichment when navigating to cart
   */
  async runInitialCheck() {
    try {
      const cart = await this.fetchCart();
      if (!cart) return;
      this.lastCartItemCount = cart.items?.length || 0;

      // Check if any items need enrichment
      const needsEnrichment = cart.items?.some(
        (item) => !item.properties?.Delivery,
      );

      // Check for duplicates
      const hasDuplicates = this.hasDuplicateVariants(cart.items || []);

      if (needsEnrichment || hasDuplicates) {
        this.scheduleEnrichment();
      }
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Wait for any ongoing enrichment to complete
   * Returns a promise that resolves when enrichment is done
   */
  waitForEnrichment() {
    if (!this.isProcessing && !this.debounceTimer) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this.enrichmentResolvers.push(resolve);
    });
  }

  /**
   * Signal that enrichment has started
   */
  startEnrichmentState() {
    window.dispatchEvent(new CustomEvent("cart:enrichment:start"));
  }

  /**
   * Signal that enrichment has completed
   */
  endEnrichmentState() {
    window.dispatchEvent(new CustomEvent("cart:enrichment:end"));

    // Resolve all waiting promises
    while (this.enrichmentResolvers.length > 0) {
      const resolve = this.enrichmentResolvers.shift();
      resolve();
    }
  }

  /**
   * Debounced enrichment - waits 500ms after last trigger before running
   */
  scheduleEnrichment() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    if (!this.enrichmentScheduled && !this.isProcessing) {
      this.enrichmentScheduled = true;
      this.startEnrichmentState();
    }

    this.debounceTimer = setTimeout(async () => {
      this.debounceTimer = null;
      await this.enrichCart();
    }, this.enrichDebounceMs);
  }

  /**
   * Initialize cart update subscriber
   */
  initCartUpdateSubscriber() {
    if (typeof subscribe === "undefined") {
      return;
    }

    subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
      if (event?.source === "cart-property-enricher") {
        return;
      }
      this.scheduleEnrichment();
    });
  }

  /**
   * Intercept fetch calls to /cart/add.js
   */
  initFetchInterceptor() {
    const originalFetch = window.fetch;
    const enricher = this;

    window.fetch = async function (...args) {
      const [url] = args;
      const response = await originalFetch.apply(this, args);

      if (
        enricher.shouldWatchCartAddUrl(url) &&
        response.ok &&
        !enricher.isProcessing
      ) {
        enricher.scheduleEnrichment();
      }

      return response;
    };
  }

  /**
   * Intercept XMLHttpRequest calls
   */
  initXHRInterceptor() {
    const enricher = this;
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this._url = url;
      return originalOpen.apply(this, [method, url, ...rest]);
    };

    XMLHttpRequest.prototype.send = function (body) {
      const xhr = this;

      xhr.addEventListener("load", function () {
        if (
          enricher.shouldWatchCartAddUrl(xhr._url) &&
          xhr.status >= 200 &&
          xhr.status < 300 &&
          !enricher.isProcessing
        ) {
          enricher.scheduleEnrichment();
        }
      });

      return originalSend.apply(this, [body]);
    };
  }

  /**
   * Poll cart every 5 seconds (reduced from 2s)
   */
  initCartPolling() {
    const enricher = this;

    const poll = async () => {
      try {
        if (!enricher.isProcessing) {
          if (!enricher.debounceTimer && !enricher.enrichmentScheduled) {
            if (document.visibilityState !== "hidden") {
              const cart = await this.fetchCart();
              if (cart) {
                const currentCount = cart.items?.length || 0;

                if (currentCount !== enricher.lastCartItemCount) {
                  enricher.lastCartItemCount = currentCount;
                  enricher.scheduleEnrichment();
                }
              }
            }
          }
        }
      } catch (error) {
        // Silent fail
      } finally {
        const nextInterval = this.isCartPage()
          ? this.cartPagePollIntervalMs
          : this.pollIntervalMs;
        setTimeout(poll, nextInterval);
      }
    };

    poll();
  }

  /**
   * Main enrichment method - optimized to minimize API calls
   */
  async enrichCart() {
    if (this.isProcessing) {
      this.pendingEnrichment = true;
      return;
    }

    this.isProcessing = true;
    this.enrichmentScheduled = false;
    let shouldScheduleAfter = false;

    try {
      const cart = await this.fetchCart();
      if (!cart?.items || cart.items.length === 0) return;

      this.lastCartItemCount = cart.items.length;

      // Check for duplicates first
      const hasDuplicates = this.hasDuplicateVariants(cart.items);

      if (hasDuplicates) {
        await this.mergeDuplicateVariants(cart.items);
        // After merge, schedule another enrichment to add delivery properties
        shouldScheduleAfter = true;
        return;
      }

      // Find items without delivery property
      const itemsNeedingDelivery = cart.items.filter(
        (item) =>
          !item.properties?.Delivery && !this.enrichingItems.has(item.key),
      );

      if (itemsNeedingDelivery.length === 0) {
        return;
      }

      // Batch get delivery properties for all items
      const updates =
        await this.getDeliveryPropertiesForItems(itemsNeedingDelivery);

      if (updates.length > 0) {
        await this.batchUpdateCartItems(updates);
      }
    } catch (error) {
      // Silent fail
    } finally {
      this.isProcessing = false;

      if (this.pendingEnrichment || shouldScheduleAfter) {
        this.pendingEnrichment = false;
        this.scheduleEnrichment();
        return;
      }

      this.endEnrichmentState();
    }
  }

  /**
   * Check if cart has duplicate variants
   */
  hasDuplicateVariants(items) {
    const seen = new Set();
    for (const item of items) {
      if (seen.has(item.variant_id)) return true;
      seen.add(item.variant_id);
    }
    return false;
  }

  /**
   * Merge duplicate variants using batch update
   */
  async mergeDuplicateVariants(items) {
    const variantGroups = new Map();

    for (const item of items) {
      if (!variantGroups.has(item.variant_id)) {
        variantGroups.set(item.variant_id, []);
      }
      variantGroups.get(item.variant_id).push(item);
    }

    // Find groups with duplicates
    const duplicateGroups = [...variantGroups.entries()].filter(
      ([, groupItems]) => groupItems.length > 1,
    );

    if (duplicateGroups.length === 0) return;

    // Build batch update: merge only identical property/selling-plan groups
    const updates = {};
    const itemsToAdd = [];

    for (const [variantId, groupItems] of duplicateGroups) {
      const signatureGroups = new Map();

      for (const item of groupItems) {
        const signature = this.getLineItemSignature(item);
        if (!signatureGroups.has(signature)) {
          signatureGroups.set(signature, []);
        }
        signatureGroups.get(signature).push(item);
      }

      for (const [, signatureItems] of signatureGroups) {
        if (signatureItems.length <= 1) continue;

        const totalQuantity = signatureItems.reduce(
          (sum, item) => sum + item.quantity,
          0,
        );
        const representative = signatureItems[0];
        const mergedProperties = this.stripDeliveryProperty(
          representative.properties,
        );
        const sellingPlanId = this.getSellingPlanId(representative);

        for (const item of signatureItems) {
          updates[item.key] = 0;
        }

        const itemToAdd = {
          id: variantId,
          quantity: totalQuantity,
          properties:
            mergedProperties && Object.keys(mergedProperties).length > 0
              ? mergedProperties
              : undefined,
        };

        if (sellingPlanId) {
          itemToAdd.selling_plan = sellingPlanId;
        }

        itemsToAdd.push(itemToAdd);
      }
    }

    if (itemsToAdd.length === 0) return;

    try {
      // Single call to remove all duplicates
      await fetch(this.buildShopifyUrl("cart/update.js"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ updates }),
      });

      // Small delay
      await new Promise((resolve) => setTimeout(resolve, 50));

      // Single call to add all merged items
      if (itemsToAdd.length > 0) {
        await fetch(this.buildShopifyUrl("cart/add.js"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({ items: itemsToAdd }),
        });
      }
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Get delivery properties for multiple items efficiently
   * Uses parallel fetching to speed up the process
   */
  async getDeliveryPropertiesForItems(items) {
    // Group by product handle to minimize API calls
    const handleGroups = new Map();
    for (const item of items) {
      if (!handleGroups.has(item.handle)) {
        handleGroups.set(item.handle, []);
      }
      handleGroups.get(item.handle).push(item);
    }

    // Fetch all product data in parallel
    const handles = [...handleGroups.keys()].filter((h) => h);
    const productDataPromises = handles.map((handle) =>
      this.getProductData(handle).then((data) => [handle, data]),
    );
    const productDataResults = await Promise.all(productDataPromises);
    const productDataMap = new Map(productDataResults);

    const updates = [];

    // Process all items using the pre-fetched data
    for (const [handle, handleItems] of handleGroups) {
      if (!handle) {
        // No handle, use fallback
        for (const item of handleItems) {
          updates.push({
            id: item.variant_id,
            key: item.key,
            quantity: item.quantity,
            properties: {
              ...item.properties,
              Delivery: this.getFallbackDeliveryMessage(),
            },
          });
        }
        continue;
      }

      const productData = productDataMap.get(handle);

      for (const item of handleItems) {
        const delivery = this.calculateDeliveryMessage(
          productData,
          item.variant_id,
          item.quantity,
        );

        if (delivery) {
          updates.push({
            id: item.variant_id,
            key: item.key,
            quantity: item.quantity,
            properties: { ...item.properties, Delivery: delivery },
          });
        }
      }
    }

    return updates;
  }

  /**
   * Calculate delivery message for a variant
   */
  calculateDeliveryMessage(productData, variantId, quantity) {
    if (!productData) {
      return this.getFallbackDeliveryMessage();
    }

    const variant = productData.variants.find((v) => v.id === variantId);
    if (!variant) {
      return this.getFallbackDeliveryMessage();
    }

    const inventory = variant.inventory_quantity ?? 0;
    const outOfStockTime = productData.outOfStockTime || "4 weeks";

    const inStockMsg = this.getSettingValue(
      "delivery_in_stock_message",
      "Delivered in approx. 1 business day",
    );
    const outOfStockMsg = this.getSettingValue(
      "delivery_out_of_stock_message",
      "Delivered in approx. {time}",
    );
    const quantityUnit = this.getSettingValue("delivery_quantity_unit", "pcs");

    // Out of stock
    if (inventory <= 0) {
      return outOfStockMsg.replace("{time}", outOfStockTime);
    }

    // Split message: some in stock, some out of stock
    if (quantity > inventory) {
      const inStockQty = inventory;
      const outOfStockQty = quantity - inventory;
      const outOfStockMsgFormatted = outOfStockMsg.replace(
        "{time}",
        outOfStockTime,
      );
      return `${inStockQty} ${quantityUnit} ${inStockMsg.toLowerCase()} and ${outOfStockQty} ${quantityUnit} ${outOfStockMsgFormatted.toLowerCase()}`;
    }

    // Fully in stock
    return inStockMsg;
  }

  /**
   * Get product data with caching
   */
  async getProductData(productHandle) {
    const cached = this.getCachedProductData(productHandle);
    if (cached) return cached;

    if (this.inflightProductRequests.has(productHandle)) {
      return this.inflightProductRequests.get(productHandle);
    }

    const requestPromise = this.fetchProductData(productHandle).finally(() => {
      this.inflightProductRequests.delete(productHandle);
    });

    this.inflightProductRequests.set(productHandle, requestPromise);
    return requestPromise;
  }

  getCachedProductData(productHandle) {
    const cached = this.productDataCache.get(productHandle);
    if (!cached) return null;

    if (cached.expiresAt < Date.now()) {
      this.productDataCache.delete(productHandle);
      return null;
    }

    return cached.data;
  }

  cacheProductData(productHandle, data) {
    const cacheTtlMs = 5 * 60 * 1000;
    this.productDataCache.set(productHandle, {
      data,
      expiresAt: Date.now() + cacheTtlMs,
    });
  }

  async fetchProductData(productHandle) {
    try {
      const response = await fetch(
        this.buildShopifyUrl(`products/${productHandle}?view=json-data`),
      );

      if (response.ok) {
        const text = await response.text();
        const jsonData = this.safeJsonParse(text);

        if (jsonData?.variants?.length > 0) {
          const result = {
            variants: jsonData.variants,
            outOfStockTime: jsonData.outOfStockTime || "4 weeks",
          };

          this.cacheProductData(productHandle, result);
          return result;
        }
      }

      const jsonResponse = await fetch(
        this.buildShopifyUrl(`products/${productHandle}.json`),
      );
      if (jsonResponse.ok) {
        const data = await jsonResponse.json();
        const result = {
          variants: data.product?.variants || [],
          outOfStockTime: "4 weeks",
        };

        this.cacheProductData(productHandle, result);
        return result;
      }
    } catch (error) {
      // Silent fail
    }

    return null;
  }

  /**
   * Get theme setting value
   */
  getSettingValue(settingKey, defaultValue) {
    try {
      if (window.theme?.settings?.[settingKey]) {
        return window.theme.settings[settingKey];
      }
    } catch {
      // Silent fail
    }
    return defaultValue;
  }

  /**
   * Fallback delivery message
   */
  getFallbackDeliveryMessage() {
    return this.getSettingValue(
      "delivery_in_stock_message",
      "Delivered in approx. 1 business day",
    );
  }

  shouldWatchCartAddUrl(url) {
    return typeof url === "string" && url.includes("/cart/add");
  }

  isCartPage() {
    return window.location?.pathname === "/cart";
  }

  getShopifyRoot() {
    return window.Shopify?.routes?.root || "/";
  }

  buildShopifyUrl(path) {
    const root = this.getShopifyRoot();
    if (path.startsWith("/")) {
      return `${root}${path.slice(1)}`;
    }
    return `${root}${path}`;
  }

  safeJsonParse(text) {
    try {
      return JSON.parse(text);
    } catch (error) {
      return null;
    }
  }

  async fetchCart() {
    try {
      const response = await fetch(this.buildShopifyUrl("cart.js"));
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  async runWithConcurrency(items, limit, task) {
    const queue = [...items];

    const workers = Array.from({ length: limit }, async () => {
      while (queue.length > 0) {
        const item = queue.shift();
        if (!item) break;
        await task(item);
      }
    });

    await Promise.all(workers);
  }

  getSellingPlanId(item) {
    return (
      item?.selling_plan_allocation?.selling_plan?.id ||
      item?.selling_plan?.id ||
      null
    );
  }

  hasSellingPlan(item) {
    return Boolean(this.getSellingPlanId(item));
  }

  stripDeliveryProperty(properties) {
    if (!properties) return null;

    const cleaned = {};
    for (const [key, value] of Object.entries(properties)) {
      if (key === "Delivery") continue;
      if (value === null || value === undefined || value === "") continue;
      cleaned[key] = value;
    }

    return cleaned;
  }

  normalizeProperties(properties) {
    const cleaned = this.stripDeliveryProperty(properties) || {};
    const entries = Object.entries(cleaned)
      .map(([key, value]) => [key, String(value)])
      .sort(([a], [b]) => a.localeCompare(b));

    return JSON.stringify(entries);
  }

  getLineItemSignature(item) {
    const sellingPlanId = this.getSellingPlanId(item) || "none";
    const propertiesSignature = this.normalizeProperties(item.properties);
    return `${item.variant_id}|${sellingPlanId}|${propertiesSignature}`;
  }

  /**
   * Batch update cart items - uses remove-then-add approach for speed
   * Only 2 API calls total instead of N calls
   */
  async batchUpdateCartItems(itemsToUpdate) {
    if (itemsToUpdate.length === 0) return;

    try {
      // Mark items as being enriched
      itemsToUpdate.forEach((item) => this.enrichingItems.add(item.key));

      const itemsWithPlan = itemsToUpdate.filter((item) =>
        this.hasSellingPlan(item),
      );
      const itemsWithoutPlan = itemsToUpdate.filter(
        (item) => !this.hasSellingPlan(item),
      );

      if (itemsWithoutPlan.length > 0) {
        // Build removal map (set all to 0)
        const updates = {};
        for (const item of itemsWithoutPlan) {
          updates[item.key] = 0;
        }

        // Single call to remove all items
        await fetch(this.buildShopifyUrl("cart/update.js"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({ updates }),
        });

        // Build items to add back with properties
        const itemsToAdd = itemsWithoutPlan.map((item) => ({
          id: item.id,
          quantity: item.quantity,
          properties: item.properties,
        }));

        // Single call to add all items back with properties
        await fetch(this.buildShopifyUrl("cart/add.js"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({ items: itemsToAdd }),
        });
      }

      if (itemsWithPlan.length > 0) {
        await this.runWithConcurrency(
          itemsWithPlan,
          this.cartChangeConcurrency,
          (item) =>
            fetch(this.buildShopifyUrl("cart/change.js"), {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-Requested-With": "XMLHttpRequest",
              },
              body: JSON.stringify({
                id: item.key,
                quantity: item.quantity,
                properties: item.properties,
              }),
            }),
        );
      }

      // Unmark items
      setTimeout(() => {
        itemsToUpdate.forEach((item) => this.enrichingItems.delete(item.key));
      }, 500);

      // Notify other components
      if (typeof publish !== "undefined") {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-property-enricher",
        });
      }
    } catch (error) {
      itemsToUpdate.forEach((item) => this.enrichingItems.delete(item.key));
    }
  }
}

// Initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    CartPropertyEnricher.instance = new CartPropertyEnricher();
  });
} else {
  CartPropertyEnricher.instance = new CartPropertyEnricher();
}
